/**
 * Findings hooks — list + promote + live SSE subscription.
 *
 * `useHypothesisFindings` reads from the REST endpoint (snapshot).
 * `useHypothesisRunEvents` opens an EventSource for live progress and
 * exposes `events` so the running detail page can render the steps
 * timeline + emerging findings.
 */

import { fetchEventSource } from '@microsoft/fetch-event-source'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { apiClient } from '@/services/api/client'
import { hypothesesApi } from '@/services/api/hypotheses'
import type { HypothesisFinding, HypothesisRunEvent } from '@/types/hypothesis-finding'
import { syncSavedInvestigation } from './sync-saved-investigation'
import { hypothesisKeys } from './use-hypotheses'

// --- Query keys ---

export const findingKeys = {
  list: (caseId: string, hypothesisId: string, runId: string) =>
    ['hypotheses', 'findings', caseId, hypothesisId, runId] as const,
}

// --- Queries ---

export function useHypothesisFindings(
  caseId: string,
  hypothesisId: string | undefined,
  runId: string | undefined
) {
  return useQuery({
    queryKey:
      hypothesisId && runId
        ? findingKeys.list(caseId, hypothesisId, runId)
        : ['hypotheses', 'findings', 'none'],
    queryFn: () => hypothesesApi.listFindings(caseId, hypothesisId as string, runId as string),
    enabled: !!caseId && !!hypothesisId && !!runId,
  })
}

// --- Mutations ---

export function usePromoteFinding(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      hypothesisId,
      runId,
      findingId,
    }: {
      hypothesisId: string
      runId: string
      findingId: string
    }) => hypothesesApi.promoteFinding(caseId, hypothesisId, runId, findingId),
    onSuccess: (inv, { hypothesisId, runId }) => {
      qc.invalidateQueries({
        queryKey: findingKeys.list(caseId, hypothesisId, runId),
      })
      syncSavedInvestigation(qc, caseId, inv)
    },
  })
}

// --- Live SSE subscription ---

interface RunEventsState {
  events: HypothesisRunEvent[]
  status: string | null
  isConnected: boolean
  error: string | null
}

/**
 * Subscribes to the SSE events stream for a run. Uses
 * `@microsoft/fetch-event-source` (not the native `EventSource`) so the
 * `Authorization: Bearer …` access-token header from `apiClient` is sent —
 * the native browser API can only carry cookies, and our auth uses
 * OAuth2PasswordBearer in headers, so EventSource always 401s.
 *
 * Closes on `run_finished` and on component unmount. Auth failures and
 * network errors abort cleanly instead of looping (the library's default
 * is to retry forever — explicit `throw` in `onerror` opts out).
 *
 * Invalidates the parent hypothesis + run + findings queries when the run
 * finishes so React Query data is fresh.
 */
export function useHypothesisRunEvents(
  caseId: string,
  hypothesisId: string | undefined,
  runId: string | undefined
): RunEventsState {
  const [state, setState] = useState<RunEventsState>({
    events: [],
    status: null,
    isConnected: false,
    error: null,
  })
  const qc = useQueryClient()

  useEffect(() => {
    if (!caseId || !hypothesisId || !runId) return

    const ctrl = new AbortController()
    let closed = false

    const url = hypothesesApi.runEventsUrl(caseId, hypothesisId, runId)
    const token = apiClient.getToken()

    fetchEventSource(url, {
      signal: ctrl.signal,
      // Disable visibility-based pause so the stream keeps running while
      // the tab is in the background.
      openWhenHidden: true,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      credentials: 'include',
      onopen: async response => {
        if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) {
          setState(s => ({ ...s, isConnected: true, error: null }))
          return
        }
        // 401/403/etc → throw so onerror runs, then we abort instead of
        // reconnecting forever.
        throw new Error(`SSE handshake failed: ${response.status} ${response.statusText}`)
      },
      onmessage: raw => {
        try {
          const evt = JSON.parse(raw.data) as HypothesisRunEvent
          setState(s => ({
            ...s,
            events: [...s.events, evt],
            status:
              evt.type === 'run_status'
                ? ((evt as { status?: string }).status ?? s.status)
                : s.status,
          }))
          if (evt.type === 'run_finished') {
            qc.invalidateQueries({ queryKey: hypothesisKeys.detail(caseId, hypothesisId) })
            qc.invalidateQueries({ queryKey: hypothesisKeys.runs(caseId, hypothesisId) })
            qc.invalidateQueries({ queryKey: findingKeys.list(caseId, hypothesisId, runId) })
            closed = true
            ctrl.abort()
          }
        } catch {
          // ignore parse errors
        }
      },
      onclose: () => {
        setState(s => ({ ...s, isConnected: false }))
      },
      onerror: err => {
        // Abort permanently — re-throwing prevents the library's default
        // exponential-retry loop. Auth failures and stream errors should
        // surface to the user, not hammer the server.
        setState(s => ({ ...s, isConnected: false, error: String(err) }))
        closed = true
        throw err
      },
    }).catch(() => {
      // Swallow the rejection from the explicit throw above — the state is
      // already updated in onerror.
    })

    return () => {
      closed = true
      ctrl.abort()
    }
  }, [caseId, hypothesisId, runId, qc])

  return state
}
